/**
 * Phase 9 Session E.2 — useDialogA11y
 *
 * Lightweight a11y helper for modal dialogs. Closes the gaps the round-2
 * audit flagged on `StartAnalysisDialog` (gap 087-R2-1) and is reusable for
 * future dialogs (DeprecateModal, etc.) so we never re-implement the same
 * trap and Esc handler twice.
 *
 * Responsibilities:
 *   1. Esc-to-close: window-level keydown listener that calls `onClose` on
 *      `event.key === 'Escape'`. Active only while the dialog is open.
 *   2. Initial focus: returns a `firstFocusableRef` that consumers attach to
 *      the element they want focused on mount (typically the first input).
 *      We also fall back to focusing the dialog root if the consumer forgets
 *      to attach `firstFocusableRef`, so keyboard users still land inside
 *      the dialog rather than on the page behind it.
 *   3. Tab focus trap: hijacks Tab / Shift+Tab so focus wraps within the
 *      dialog. We re-query focusable descendants on each Tab — that way
 *      lazily-rendered elements (e.g. the budget-exceeded banner that only
 *      shows after a 402) are picked up automatically without re-mounting
 *      the trap.
 *
 * Intentional non-goals (kept simple to honour the no-new-deps constraint):
 *   - No focus restoration on close. The trigger button is the natural next
 *     focus target and React's default focus behaviour handles that well
 *     enough for our flow. We can add it later if a dialog ever opens
 *     itself via a non-button trigger.
 *   - No `inert`/scroll-lock on the rest of the page. The fixed-position
 *     backdrop already swallows clicks, and the trap prevents Tab from
 *     escaping. AT users on iOS with VoiceOver may still swipe past the
 *     dialog — acceptable for v1.
 */
import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

interface UseDialogA11yOptions {
  /** Called when the user presses Escape. */
  onClose: () => void;
  /**
   * When `false` the hook is a no-op. Keeps the call site declarative for
   * dialogs that mount/unmount based on local state — but we still expose
   * the flag so consumers can keep the hook above an early `return` without
   * conditionally calling it.
   */
  isOpen: boolean;
}

interface UseDialogA11yReturn {
  /** Attach to the dialog root element (e.g. the `<div role="dialog">`). */
  dialogRef: RefObject<HTMLDivElement>;
  /** Attach to the element you want focused on mount (typically the first input). */
  firstFocusableRef: RefObject<HTMLElement>;
}

/**
 * Selector for "tabbable" descendants. Mirrors what react-focus-lock and
 * focus-trap use as a baseline — covers links, buttons, inputs, selects,
 * textareas, and any element with an explicit positive/zero tabIndex. We
 * filter out `[disabled]` and `[aria-hidden="true"]` at runtime since the
 * `:not()` chain doesn't compose with attribute matching reliably across
 * the inputs we care about (e.g. native checkboxes).
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function getTabbables(root: HTMLElement): HTMLElement[] {
  const nodes = Array.from(
    root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  );
  return nodes.filter((el) => {
    if (el.hasAttribute('disabled')) return false;
    if (el.getAttribute('aria-hidden') === 'true') return false;
    // Type-narrow guard: HTMLInputElement.type === 'hidden' is not focusable.
    if (el instanceof HTMLInputElement && el.type === 'hidden') return false;
    // `tabIndex` is -1 explicitly excluded above via the selector but we
    // also defensively skip elements that have been rendered with display:none
    // — `offsetParent` is null for those (modulo `position:fixed` quirks,
    // which are rare in our dialog content).
    if (el.offsetParent === null && getComputedStyle(el).position !== 'fixed') {
      return false;
    }
    return true;
  });
}

export function useDialogA11y({
  onClose,
  isOpen,
}: UseDialogA11yOptions): UseDialogA11yReturn {
  // Note: `as` cast is required because `useRef<T>(null)` returns
  // `RefObject<T>` (where current is read-only) only when initialized with
  // `null` AND no override. We want a mutable ref that React still treats as
  // a valid ref prop, hence the explicit object-with-current pattern.
  const dialogRef = useRef<HTMLDivElement>(null) as RefObject<HTMLDivElement>;
  const firstFocusableRef = useRef<HTMLElement>(
    null,
  ) as RefObject<HTMLElement>;

  // Esc-to-close. Window-level listener so the user doesn't need focus
  // inside the dialog for Esc to work (matches native <dialog> semantics).
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Initial focus: prefer the explicitly-tagged element, fall back to the
  // first tabbable inside the dialog, then the dialog root itself.
  useEffect(() => {
    if (!isOpen) return;
    // rAF to wait for the DOM to settle (modal is rendered into a portal-
    // free fixed container in our case, but Tailwind transitions/measure
    // sometimes still layout-shift on the same tick).
    const raf = requestAnimationFrame(() => {
      const explicit = firstFocusableRef.current;
      if (explicit) {
        explicit.focus();
        return;
      }
      const root = dialogRef.current;
      if (!root) return;
      const tabbables = getTabbables(root);
      if (tabbables.length > 0) {
        tabbables[0].focus();
      } else {
        root.focus();
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [isOpen]);

  // Focus trap on Tab / Shift+Tab. Re-query each press so newly-mounted
  // elements (e.g. the budget-exceeded banner) become reachable.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const root = dialogRef.current;
      if (!root) return;
      const tabbables = getTabbables(root);
      if (tabbables.length === 0) {
        e.preventDefault();
        root.focus();
        return;
      }
      const first = tabbables[0];
      const last = tabbables[tabbables.length - 1];
      const active = document.activeElement;

      if (e.shiftKey) {
        if (active === first || !root.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last || !root.contains(active)) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    // Bind to the dialog root so the trap doesn't compete with any
    // app-level shortcut handler attached to window. If focus has somehow
    // escaped the root we still see the event because document-level
    // bubbling reaches us before any global handler can react.
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  return { dialogRef, firstFocusableRef };
}
